/**
 * Public URLs for a live marketplace listing.
 *
 * Built from the marketplace's OWN id (ASIN / eBay ItemID / Walmart item id /
 * Shopify variant id), never the seller SKU — see ARCHITECTURE.md §1 for why
 * those are different identifiers. Returns null when we can't form a real
 * URL, so callers render plain text instead of a link that 404s.
 *
 * These mirror the URL shapes already used server-side in each marketplace
 * client's `fetchListingDetail` (e.g. amazon.ts's `/dp/{asin}`, ebay.ts's
 * `/itm/{ItemID}`), kept here so the browser can link out without a round
 * trip.
 */
export function marketplaceListingUrl(
  marketplace: string,
  marketplaceId: string | null,
  opts?: { shopDomain?: string | null; channelUrl?: string | null },
): string | null {
  const id = marketplaceId?.trim();
  switch (marketplace) {
    case "amazon":
      // Domain deliberately fixed to .com: this account's marketplace ids are
      // ASINs, and amazon.com resolves an ASIN even when the listing's home
      // marketplace is MX/CA/BR (it redirects rather than 404s).
      return id ? `https://www.amazon.com/dp/${encodeURIComponent(id)}` : null;
    case "ebay":
      return id ? `https://www.ebay.com/itm/${encodeURIComponent(id)}` : null;
    case "walmart":
      return id ? `https://www.walmart.com/ip/${encodeURIComponent(id)}` : null;
    case "shopify": {
      // A Shopify variant id has no public storefront URL of its own, so the
      // useful destination is the admin page for it. Needs the shop domain;
      // fall back to the channel's configured storefront URL, then to null.
      const shop = opts?.shopDomain?.trim();
      if (id && shop)
        return `https://${shop}/admin/products?selectedView=all&query=${encodeURIComponent(id)}`;
      return opts?.channelUrl?.trim() || null;
    }
    default:
      return null;
  }
}
